// File: frontend/packages/shared/services/src/stripeConnectService.js
//
// Client wrapper around the backend Stripe Connect endpoints. The
// shared apiService interceptor attaches the JWT + x-business-id
// header for us — these functions don't have to manage either.
//
// Both endpoints are owner-only on the backend. Non-owners get a
// 403 with a `message`; the Console page surfaces the message as
// the error UI.

import { api } from "@upryzze/services";

/**
 * Get or create the Stripe Connect Express account for the active
 * business. Idempotent server-side. The response includes the
 * platform `publishableKey` so the caller can feed it into
 * `loadConnectAndInitialize()` to bootstrap the Connect Embedded
 * Components SDK.
 */
export const getOrCreateStripeConnectAccount = () =>
  api.post("/v2/stripe-connect/account");

/**
 * Mint a short-lived AccountSession `client_secret` scoped to the
 * requested Connect Embedded Components. Stripe's SDK calls this
 * automatically via the `fetchClientSecret` callback passed to
 * `loadConnectAndInitialize` — UI code rarely invokes it directly.
 *
 * @param {string[]} components  any subset of the backend allowlist:
 *   account_onboarding, payouts, account_management, notification_banner.
 */
export const createStripeConnectAccountSession = (components) =>
  api.post("/v2/stripe-connect/account-session", { components });

/**
 * Balance + payout state for the Payments dashboard hero: available /
 * pending / instant-available balances, in-transit payouts, next
 * payout, bank account (name + last4), schedule, recent payouts.
 * businessadmin-only; the dashboard degrades gracefully on 403.
 */
export const getStripeConnectPayoutOverview = () =>
  api.get("/v2/stripe-connect/overview");

/**
 * Instant payout from the connected account's available balance.
 * `requestId` is a client-generated UUID; the backend forwards it as
 * the Stripe idempotency key so a retry can't create two payouts.
 *
 * @param {{ amountCents: number, requestId: string }} args
 */
export const createStripeConnectInstantPayout = ({ amountCents, requestId }) =>
  api.post("/v2/stripe-connect/payouts/instant", { amountCents, requestId });

/**
 * Tenant-wide payments dashboard aggregations (webhook-mirrored
 * CrmPayment rows): 30d collected/fees/net, refunds, open disputes,
 * avg time-to-pay, recent payments table. Readable by all CRM roles.
 */
export const getCrmPaymentsDashboard = () =>
  api.get("/v2/crm/payments/dashboard");
